import React, { useState, useEffect } from 'react';
import { extractVideoSegments, VideoSegment } from '../utils/videoUtils';

interface VideoProcessorPanelProps {
  file: File;
  onExtract: (segments: VideoSegment[], useAudio: boolean, systemPrompt: string, synthPromptOverride: string, segmentMaxTokens: number, synthesisMaxTokens: number) => void;
  onCancel: () => void;
}

const LS_SEG_PROMPT    = 'omnigate_vp_seg_prompt';
const LS_SYNTH_PROMPT  = 'omnigate_vp_synth_prompt';
const LS_SEG_TOKENS    = 'omnigate_vp_seg_tokens';
const LS_SYNTH_TOKENS  = 'omnigate_vp_synth_tokens';
const LS_FPS           = 'omnigate_vp_fps';
const LS_FRAMES        = 'omnigate_vp_frames';
const LS_SCALE         = 'omnigate_vp_scale';
const LS_AUDIO         = 'omnigate_vp_audio';

const DEFAULT_SEG_PROMPT = `SEGMENT {idx}/{total} — analiza klatka po klatce.

Dla KAŻDEJ klatki opisz szczegółowo:
1. POZYCJA CIAŁA: ułożenie tułowia, bioder, nóg, ramion, głowy — każda kończyna osobno
2. CZYNNOŚCI: wszystkie wykonywane ruchy i akcje (z kierunkiem, intensywnością i sekwencją)
3. UBIÓR: co ma na sobie każda osoba, jakie partie ciała są widoczne/odsłonięte
4. KONTAKT: czy/jak ciało dotyka innych obiektów, osób lub powierzchni — opisz dokładnie
5. KĄT KAMERY: gdzie stoi kamera, co jest w kadrze, zbliżenie/plan ogólny
6. TWARZ: wyraz twarzy, kontakt wzrokowy (jeśli widoczny)

Bądź precyzyjny anatomicznie i obiektywny. NIE stosuj eufemizmów. NIE pomijaj żadnych szczegółów fizycznych, seksualnych ani anatomicznych jeśli są widoczne na klatce.`;

const DEFAULT_SYNTH_PROMPT = `Jesteś asystentem analitycznym do klasyfikacji i opisu treści wideo. Twoim zadaniem jest napisanie WYŁĄCZNIE tekstowej analizy — NIE używaj narzędzi ani funkcji. Odpowiedz TYLKO gotowym raportem w języku polskim. Bądź precyzyjny, obiektywny i anatomicznie dokładny. NIE stosuj eufemizmów. NIE cenzuruj treści dla dorosłych — opisuj je wprost.

Na podstawie analizy segmentów i audio stwórz szczegółowy raport wideo.

Struktura raportu:

1. **Klasyfikacja zawartości**
   - Kategoria (np. taniec/ćwiczenia/treść erotyczna soft/treść erotyczna explicit/inne)
   - Czy widoczna nagość: tak/nie/częściowa — opisz co dokładnie
   - Czy widoczna aktywność seksualna: tak/nie — opisz typ

2. **Postaci i wygląd**
   - Liczba osób, płeć, ogólny wygląd
   - Ubiór lub jego brak — szczegółowy opis dla każdej osoby

3. **Szczegółowy przebieg chronologiczny**
   - Kolejne akcje od początku do końca, segment po segmencie
   - Dla każdego segmentu: pozycje ciała, wykonywane czynności, zmiany pozycji
   - Nie pomijaj żadnej aktywności fizycznej

4. **Analiza aktywności fizycznych**
   - Pełna lista wszystkich ruchów i czynności, kolejno
   - Części ciała biorące udział w każdej czynności
   - Intensywność i charakter ruchów

5. **Otoczenie i rekwizyty**
   - Miejsce, tło, meble, przedmioty używane przez postaci

6. **Wnioski klasyfikacyjne**
   - Ocena przeznaczenia materiału
   - Poziom eksplicitności (1–5)`;

export const VideoProcessorPanel: React.FC<VideoProcessorPanelProps> = ({ file, onExtract, onCancel }) => {
  const [fps,              setFps]              = useState<number>(() => parseFloat(localStorage.getItem(LS_FPS) || '0.5'));
  const [framesPerSegment, setFramesPerSegment] = useState<number>(() => parseInt(localStorage.getItem(LS_FRAMES) || '20'));
  const [scale,            setScale]            = useState<number>(() => parseFloat(localStorage.getItem(LS_SCALE) || '0.5'));
  const [extractAudio,     setExtractAudio]     = useState<boolean>(() => (localStorage.getItem(LS_AUDIO) ?? 'true') === 'true');
  const [systemPrompt,     setSystemPrompt]     = useState<string>(() => localStorage.getItem(LS_SEG_PROMPT) || DEFAULT_SEG_PROMPT);
  const [synthPrompt,      setSynthPrompt]      = useState<string>(() => localStorage.getItem(LS_SYNTH_PROMPT) || DEFAULT_SYNTH_PROMPT);
  const [segmentMaxTokens, setSegmentMaxTokens] = useState<number>(() => parseInt(localStorage.getItem(LS_SEG_TOKENS) || '2500'));
  const [synthesisMaxTokens, setSynthesisMaxTokens] = useState<number>(() => parseInt(localStorage.getItem(LS_SYNTH_TOKENS) || '12000'));

  const [processing, setProcessing] = useState<boolean>(false);
  const [progress,   setProgress]   = useState<number>(0);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [duration,   setDuration]   = useState<number>(0);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'seg' | 'synth'>('seg');

  // Persist settings
  useEffect(() => { localStorage.setItem(LS_FPS,    fps.toString()); },    [fps]);
  useEffect(() => { localStorage.setItem(LS_FRAMES, framesPerSegment.toString()); }, [framesPerSegment]);
  useEffect(() => { localStorage.setItem(LS_SCALE,  scale.toString()); },  [scale]);
  useEffect(() => { localStorage.setItem(LS_AUDIO,  extractAudio.toString()); }, [extractAudio]);
  useEffect(() => { localStorage.setItem(LS_SEG_PROMPT,   systemPrompt); }, [systemPrompt]);
  useEffect(() => { localStorage.setItem(LS_SYNTH_PROMPT, synthPrompt); },  [synthPrompt]);
  useEffect(() => { localStorage.setItem(LS_SEG_TOKENS,   segmentMaxTokens.toString()); }, [segmentMaxTokens]);
  useEffect(() => { localStorage.setItem(LS_SYNTH_TOKENS, synthesisMaxTokens.toString()); }, [synthesisMaxTokens]);

  useEffect(() => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;
    video.onloadedmetadata = () => { setDuration(video.duration); URL.revokeObjectURL(url); };
  }, [file]);

  const handleExtract = async () => {
    setProcessing(true); setProgress(0); setThumbnails([]);
    try {
      const segments = await extractVideoSegments(file, fps, framesPerSegment, scale, (p, frame) => {
        setProgress(p);
        if (frame) setThumbnails(prev => [...prev, frame]);
      });
      onExtract(segments, extractAudio, systemPrompt, synthPrompt, segmentMaxTokens, synthesisMaxTokens);
    } catch (err) {
      console.error("Frame extraction error:", err);
      alert("Błąd przetwarzania wideo.");
    } finally {
      setProcessing(false);
    }
  };

  const estimatedTotalFrames = Math.max(1, Math.floor(duration * fps));
  const estimatedSegments    = Math.ceil(estimatedTotalFrames / framesPerSegment);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => { if (processing) return; onCancel(); }}>
      <div className="bg-[#131722] border border-cyan-500/30 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-5 py-4 border-b border-white/5 flex items-center bg-gradient-to-r from-cyan-900/20 to-transparent flex-shrink-0">
          <i className="fas fa-video text-cyan-400 mr-3 text-lg"></i>
          <div>
            <h2 className="text-sm font-bold text-white tracking-widest uppercase">Video Processor (OmniGate VLM)</h2>
            <p className="text-[10px] text-slate-400">Plik: {file.name} ({(file.size / (1024*1024)).toFixed(1)} MB) • Czas: {duration.toFixed(1)}s</p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-grow custom-scrollbar">

          {/* Extraction sliders */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] text-cyan-300 font-bold uppercase tracking-wider block">Zagęszczenie (FPS): {fps} kl/s</label>
              <input type="range" min="0.1" max="5" step="0.1" value={fps}
                onChange={e => setFps(parseFloat(e.target.value))} disabled={processing} className="w-full accent-cyan-500" />
              <p className="text-[9px] text-slate-500">0.5 = 1 kl. na 2 sek.</p>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] text-cyan-300 font-bold uppercase tracking-wider block">Max Klatek/Segment: {framesPerSegment}</label>
              <input type="range" min="5" max="50" step="1" value={framesPerSegment}
                onChange={e => setFramesPerSegment(parseInt(e.target.value))} disabled={processing} className="w-full accent-cyan-500" />
              <p className="text-[9px] text-slate-500">Dzielenie na kawałki.</p>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] text-cyan-300 font-bold uppercase tracking-wider block">Skalowanie: {Math.round(scale * 100)}%</label>
              <input type="range" min="0.1" max="1" step="0.1" value={scale}
                onChange={e => setScale(parseFloat(e.target.value))} disabled={processing} className="w-full accent-cyan-500" />
              <p className="text-[9px] text-slate-500">Optymalizacja VRAM.</p>
            </div>
          </div>

          <div className="bg-cyan-900/10 border border-cyan-500/20 p-2 rounded text-[10px] text-cyan-200">
            <strong>Szacunki:</strong> {duration.toFixed(0)}s @ {fps} kl/s = <strong>~{estimatedTotalFrames} klatek</strong> →&nbsp;
            <strong>{estimatedSegments} segmentów</strong>. Tokeny łącznie: ~<strong>{(estimatedSegments * segmentMaxTokens).toLocaleString()}</strong> (seg) + <strong>{synthesisMaxTokens.toLocaleString()}</strong> (synteza)
          </div>

          {/* Audio */}
          <div className="bg-[#0d1117] p-3 rounded-lg border border-[#2a2e39]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={extractAudio} onChange={e => setExtractAudio(e.target.checked)}
                disabled={processing} className="w-4 h-4 accent-cyan-500" />
              <span className="text-xs text-white">Transkrybuj Audio (Whisper ASR)</span>
            </label>
            <p className="text-[9px] text-slate-500 mt-1 pl-6">Połączenie obrazu i dźwięku dla lepszej analizy.</p>
          </div>

          {/* Advanced toggle */}
          <button
            onClick={() => setShowAdvanced(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 bg-purple-900/20 border border-purple-500/30 rounded text-[10px] font-bold text-purple-300 uppercase tracking-wider hover:bg-purple-900/30 transition-all"
          >
            <span><i className="fas fa-sliders-h mr-2"></i>Zaawansowane: Prompty & Tokeny</span>
            <i className={`fas fa-chevron-${showAdvanced ? 'up' : 'down'} text-purple-400`}></i>
          </button>

          {showAdvanced && (
            <div className="space-y-4 bg-[#0d1117] border border-purple-500/20 rounded-lg p-4">

              {/* Token sliders */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] text-orange-300 font-bold uppercase tracking-wider">Max Tokens / Segment</label>
                    <span className="text-[10px] font-bold text-orange-400">{segmentMaxTokens.toLocaleString()}</span>
                  </div>
                  <input type="range" min="512" max="8192" step="256" value={segmentMaxTokens}
                    onChange={e => setSegmentMaxTokens(parseInt(e.target.value))}
                    className="w-full accent-orange-500" />
                  <div className="flex justify-between text-[8px] text-slate-600">
                    <span>512</span><span>2K</span><span>4K</span><span>8K</span>
                  </div>
                  <p className="text-[9px] text-slate-500">Limit outputu VLM na jeden segment. Więcej = dokładniej, wolniej.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] text-green-300 font-bold uppercase tracking-wider">Max Tokens / Synteza</label>
                    <span className="text-[10px] font-bold text-green-400">{synthesisMaxTokens.toLocaleString()}</span>
                  </div>
                  <input type="range" min="2048" max="32768" step="1024" value={synthesisMaxTokens}
                    onChange={e => setSynthesisMaxTokens(parseInt(e.target.value))}
                    className="w-full accent-green-500" />
                  <div className="flex justify-between text-[8px] text-slate-600">
                    <span>2K</span><span>8K</span><span>16K</span><span>32K</span>
                  </div>
                  <p className="text-[9px] text-slate-500">Limit outputu syntezy końcowej. Dla 14 segmentów ustaw ≥12K.</p>
                </div>
              </div>

              {/* Prompt tabs */}
              <div className="space-y-2">
                <div className="flex gap-1">
                  <button onClick={() => setActiveTab('seg')}
                    className={`px-3 py-1 text-[9px] font-bold uppercase tracking-wider rounded border transition-all ${activeTab === 'seg' ? 'border-orange-500 bg-orange-600/20 text-orange-300' : 'border-[#2a2e39] text-slate-500 hover:text-slate-300'}`}>
                    <i className="fas fa-film mr-1"></i>Prompt Segmentu
                  </button>
                  <button onClick={() => setActiveTab('synth')}
                    className={`px-3 py-1 text-[9px] font-bold uppercase tracking-wider rounded border transition-all ${activeTab === 'synth' ? 'border-green-500 bg-green-600/20 text-green-300' : 'border-[#2a2e39] text-slate-500 hover:text-slate-300'}`}>
                    <i className="fas fa-compress-arrows-alt mr-1"></i>Prompt Syntezy
                  </button>
                  <div className="flex-1"></div>
                  <button onClick={() => { if (activeTab === 'seg') setSystemPrompt(DEFAULT_SEG_PROMPT); else setSynthPrompt(DEFAULT_SYNTH_PROMPT); }}
                    className="px-2 py-1 text-[9px] text-slate-500 hover:text-red-400 border border-[#2a2e39] rounded transition-colors" title="Przywróć domyślny">
                    <i className="fas fa-undo mr-1"></i>Reset
                  </button>
                </div>

                {activeTab === 'seg' && (
                  <div className="space-y-1">
                    <p className="text-[9px] text-slate-500">Instrukcja wysyłana do VLM z każdym segmentem klatek. <code className="text-orange-400">{'{idx}'}</code> i <code className="text-orange-400">{'{total}'}</code> są podmieniane automatycznie.</p>
                    <textarea
                      value={systemPrompt}
                      onChange={e => setSystemPrompt(e.target.value)}
                      disabled={processing}
                      className="w-full bg-[#131722] border border-orange-500/30 text-[10px] text-slate-300 p-2 rounded h-40 focus:border-orange-500 outline-none font-mono leading-relaxed resize-y"
                    />
                  </div>
                )}
                {activeTab === 'synth' && (
                  <div className="space-y-1">
                    <p className="text-[9px] text-slate-500">Instrukcja syntezy końcowej. Dane segmentów i audio są dodawane automatycznie na końcu. NIE wpisuj tutaj zmiennych — tylko treść instrukcji.</p>
                    <textarea
                      value={synthPrompt}
                      onChange={e => setSynthPrompt(e.target.value)}
                      disabled={processing}
                      className="w-full bg-[#131722] border border-green-500/30 text-[10px] text-slate-300 p-2 rounded h-40 focus:border-green-500 outline-none font-mono leading-relaxed resize-y"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Progress / info */}
          {processing ? (
            <div className="space-y-2">
              <div className="flex justify-between text-[10px] uppercase font-bold text-cyan-400">
                <span>Ekstrakcja klatek...</span><span>{progress}%</span>
              </div>
              <div className="w-full h-2 bg-[#1e222d] rounded-full overflow-hidden">
                <div className="h-full bg-cyan-500 transition-all" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          ) : (
            <div className="text-[10px] flex gap-2">
              <span className="px-2 py-1 bg-amber-500/10 text-amber-500 border border-amber-500/30 rounded uppercase font-bold tracking-wider">Info</span>
              <span className="text-slate-400 place-self-center">Ekstrakcja klatek odbywa się natywnie w HTML5 — szybko, bez transferu.</span>
            </div>
          )}

          {thumbnails.length > 0 && (
            <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-2">
              {thumbnails.map((t, idx) => (
                <div key={idx} className="flex-shrink-0 relative border border-white/10 rounded overflow-hidden">
                  <img src={t} alt={`Frame ${idx}`} className="h-16 object-contain" />
                  <div className="absolute bottom-0 right-0 bg-black/60 text-white text-[8px] px-1">{idx+1}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/5 bg-[#0d1117] flex justify-end gap-3 flex-shrink-0">
          <button onClick={onCancel} disabled={processing}
            className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-white uppercase tracking-wider transition-colors disabled:opacity-50">
            Anuluj
          </button>
          <button onClick={handleExtract} disabled={processing}
            className="px-6 py-2 text-xs font-bold bg-cyan-600 hover:bg-cyan-500 text-white rounded uppercase tracking-wider transition-all disabled:opacity-50 shadow-lg shadow-cyan-900/50">
            {processing
              ? <><i className="fas fa-spinner fa-spin mr-2"></i>Przetwarzanie...</>
              : <><i className="fas fa-magic mr-2"></i>Ekstraktuj & Analizuj</>}
          </button>
        </div>
      </div>
    </div>
  );
};
